import type { TelemetrySummary, TelemetryVital } from '@/apis/telemetry';
import { DEFAULT_CHARTS } from './measures';

/** Which part of the shipment's readings a request covers. */
export enum ChunkName {
  BeforePickup = 'beforePickup',
  Operational = 'operational',
  AfterDelivery = 'afterDelivery',
}

/** One series request window, [from, to) or [from, to] when `includeEnd`. */
export interface SeriesChunk {
  name: ChunkName;
  from: number;
  to: number;
  includeEnd: boolean;
}

/**
 * The windows to fetch, in time order: before pickup, the operational window, after delivery.
 * Every reading lands in exactly one; only the last chunk includes its end. An empty window is skipped.
 */
export const seriesChunks = (
  anchors: TelemetrySummary['anchors'],
  now: number
): SeriesChunk[] => {
  const { picked_up, delivered, first_reading, last_reading } = anchors;
  const start = picked_up ?? first_reading;
  if (start === null || first_reading === null) return [];
  const chunks: SeriesChunk[] = [];
  if (picked_up !== null && first_reading < picked_up) {
    chunks.push({
      name: ChunkName.BeforePickup,
      from: first_reading,
      to: picked_up,
      includeEnd: false,
    });
  }
  const end = delivered ?? Math.max(now, last_reading ?? now);
  if (start < end || (delivered === null && start === end)) {
    chunks.push({
      name: ChunkName.Operational,
      from: start,
      to: end,
      includeEnd: delivered === null,
    });
  }
  if (
    delivered !== null &&
    last_reading !== null &&
    last_reading >= delivered
  ) {
    chunks.push({
      name: ChunkName.AfterDelivery,
      from: delivered,
      to: last_reading,
      includeEnd: true,
    });
  }
  return chunks;
};

/**
 * Groups measures into requests: the default charts together, each other selected chart alone,
 * so ticking Pressure or Light later fetches only that measure.
 */
export const measureGroups = (
  selected: readonly TelemetryVital[],
  available: readonly TelemetryVital[]
): TelemetryVital[][] => {
  const base = DEFAULT_CHARTS.filter((key) => available.includes(key));
  const extras = selected.filter(
    (key) => !DEFAULT_CHARTS.includes(key) && available.includes(key)
  );
  return [...(base.length ? [base] : []), ...extras.map((key) => [key])];
};
